// One task group (docs/12 "Admin web UI"): its facts, the requirements with how each is bound, its
// tasks and its costs. Its owner edits, pauses, resumes or archives it here (S112) or in the apps;
// admins see other users' groups read-only.
import { ListBullets } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router";

import { accessQuery } from "../auth/access";
import { ChatButton } from "../chat/ChatButton";

import { versionQuery } from "../catalog/api";
import { parseGraph } from "../catalog/parse";
import { groupTasksQuery, taskGroupQuery } from "../tasks/api";
import { useT } from "../i18n";
import { GROUP_TONE } from "../tasks/display";
import { GroupActions } from "../tasks/group/GroupActions";
import { GroupCostsCard } from "../tasks/group/GroupCostsCard";
import { GroupSummary } from "../tasks/group/GroupSummary";
import { GroupNetworkCard } from "../network/GroupNetworkCard";
import { refetchInterval, useLiveTasks } from "../tasks/live";
import { LiveBadge } from "../tasks/LiveBadge";
import { RequirementsCard } from "../tasks/group/RequirementsCard";
import { ResourcesCard } from "../tasks/group/ResourcesCard";
import { SharedFilesCard } from "../tasks/group/SharedFilesCard";
import { ToolchainsCard } from "../tasks/group/ToolchainsCard";
import { TasksTable } from "../tasks/group/TasksTable";
import { useLookups } from "../tasks/lookups";
import { WidgetCard } from "../ui/Card";
import { PageHeader } from "../ui/PageHeader";
import { EmptyState, ErrorState, Loading } from "../ui/states";
import { StatusPill } from "../ui/status";
import { detailCrumbs } from "../layout/nav";

export default function TaskGroupDetailPage() {
  const { tasks: copy, shell } = useT();
  const t = copy.group;
  const crumbs = (name: string) => detailCrumbs("taskGroups", "/task-groups", { name });
  const { groupId = "" } = useParams();
  const live = useLiveTasks({ groups: [groupId] });
  const group = useQuery({ ...taskGroupQuery(groupId), refetchInterval: refetchInterval(live) });
  const tasks = useQuery({ ...groupTasksQuery(groupId), refetchInterval: refetchInterval(live) });
  const lookups = useLookups();
  const me = useQuery(accessQuery()).data;
  const pinned = group.data ? lookups.version(group.data.workflow_version_id) : undefined;
  const version = useQuery({ ...versionQuery(pinned?.workflowId ?? "", pinned?.version ?? 0), enabled: pinned !== undefined });
  const graph = version.data ? parseGraph(version.data.definition) : undefined;

  if (group.isPending) return <Loading />;
  if (group.isError) {
    return (
      <>
        <PageHeader title={t.title} breadcrumb={crumbs(shell.pages.notFound.title)} />
        <ErrorState title={t.loadFailed} message={group.error.message} />
      </>
    );
  }
  const g = group.data;
  return (
    <>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            {g.name} <StatusPill tone={GROUP_TONE[g.status]} status={g.status} />
          </span>
        }
        breadcrumb={crumbs(g.name)}
        description={t.ownedBy(lookups.owner(g.owner_user_id))}
      >
        <LiveBadge live={live} />
        {me?.id === g.owner_user_id ? <ChatButton to={`/task-groups/${g.id}/chat`} /> : null}
        {me?.id === g.owner_user_id ? <GroupActions group={g} /> : null}
      </PageHeader>
      <div className="space-y-5">
        <GroupSummary group={g} lookups={lookups} />
        <div className="grid gap-5 xl:grid-cols-12">
          <WidgetCard className="min-w-0 xl:col-span-8" title={copy.columns.tasks} description={t.tasksDescription}>
            {tasks.isPending ? <Loading /> : null}
            {tasks.isError ? <ErrorState message={tasks.error.message} /> : null}
            {tasks.data?.length === 0 ? (
              <EmptyState compact icon={<ListBullets weight="duotone" />} title={t.noTasksYet}>
                {g.source === "manual" ? t.noTasksManual : t.noTasksPolled}
              </EmptyState>
            ) : null}
            {tasks.data && tasks.data.length > 0 ? <TasksTable tasks={tasks.data} graph={graph} /> : null}
          </WidgetCard>
          <div className="min-w-0 space-y-5 xl:col-span-4">
            <ResourcesCard group={g} />
            <GroupNetworkCard groupId={g.id} />
            <GroupCostsCard groupId={g.id} />
            <RequirementsCard groupId={g.id} />
            <ToolchainsCard group={g} />
            <SharedFilesCard group={g} />
          </div>
        </div>
      </div>
    </>
  );
}
